import React from "react";
import moment from "moment";
import { Formik, FastField } from "formik";
import {
  Button,
  ContentSection,
  DataOverview,
  Fieldset,
  Form,
  Hr,
  Layout,
  Paragraph,
  SectionHeading,
  ShowMore,
  Spinner,
  TileSelect,
} from "@vwfs-bronson/bronson-react";
import { Optional } from "../../common/Optional";
import { Output } from "../../Text";
import { DefinitionList } from "../../components/SummaryPage/DefinitionList";
import { CheckboxField } from "../../formik";
import { isEuCitizen } from "../../common/countryCheck";
import { Route } from "../../common/Navigation";
import { Analytics } from "../../services/analytics";
import { setActualPage } from "../page.utils";
import EmailValidationModal from '../../common/components/EmailValidationModal/EmailValidationModal';
import { scrollToElement } from '../../utils';
import { finishFullAppForm } from '../../services/api/finishFullAppForm';
import { setDataPrivacy } from '../../services/analytics/functions';
import { isVtiBOP } from '../../common/JourneyType';

const getGermanDate = (value: string) =>
  value ? moment(value).format("DD.MM.YYYY") : undefined;

const formatEuroAmount = (value: number) => `${value} €`;

const getValue = (array: any[], key: string) => {
  return Optional.of(key)
    .map((k: any) => array.find((v) => v.key === k))
    .map((v: any) => v.value)
    .getOrUndefined();
};

const chunkString = (str: string, length: number) => {
  return str.match(new RegExp(".{1," + length + "}", "g"));
};

const formatIban = (value: string) => {
  return chunkString(value, 4)?.join(" ");
};

class SummaryPage extends React.Component<SummaryPageProps, SummaryPageState> {
  checkBoxRef: React.RefObject<any>;

  constructor(props: SummaryPageProps) {
    super(props);

    this.state = {
      values: {
        allowAllAds: false,
        allowPhoneAds: false,
        allowMailAds: false,
        allowPersonalizedMarketing: false,
        allowUnsafeMailCommunication: this.props.tlsSupport,
      },
      showEmailValidationModal: false,
      isLoading: false,
      formValues: null
    };
    this.checkBoxRef = React.createRef();
  }

  pageName = "Summary page";

  defaultTrackingData = {
    [Analytics.keys.pageName()]: this.pageName,
    [Analytics.keys.viewChange()]: this.pageName,
  };

  trackPage = () => {
    setTimeout(() => {
      const pageData = {
        [Analytics.keys.eventType(0)]: "pageView",
        [Analytics.keys.eventAction(0)]: "Success",
        [Analytics.keys.eventType(1)]: "SummaryPage",
        [Analytics.keys.eventAction(1)]: "Success",
      };
      Analytics.setTrackingData({ ...this.defaultTrackingData, ...pageData });
      Analytics.trackPage();
    }, 1000);
  };

  trackTLSCheck = (confirm: boolean) => {
    const { tlsSupport } = this.props;
    if (tlsSupport === false) {
      const tlsData = {
        [Analytics.keys.eventType(0)]: "interAction",
        [Analytics.keys.eventAction(0)]: "Success",
        [Analytics.keys.linkInformation(0)]: `TLS check${
          confirm ? ": Confirmed" : ""
        }`,
      };
      Analytics.setTrackingData({ ...this.defaultTrackingData, ...tlsData });
      Analytics.trackInteraction();
    }
  };

  getSectionName = (index: number) => {
    const sectionName =
      [
        "Personal data", // 0
        "Contact data", // 1
        "Employment data", // 2
        "Financial data", // 3
        "Bank account data", // 4
        "Document upload", // 5 (LCV only)
        "Summary", // 6 (LCV only)
      ][index] || "";
    return sectionName;
  };

  trackEditButton = (section: number) => {
    const editData = {
      [Analytics.keys.eventType(0)]: "interAction",
      [Analytics.keys.eventAction(0)]: "Success",
      [Analytics.keys.linkInformation(0)]: `Edit ${this.getSectionName(
        section
      )}`,
    };
    Analytics.setTrackingData({ ...this.defaultTrackingData, ...editData });
    Analytics.trackInteraction();
  };

  onChangeTLS = (e: any) => {
    if (e?.target?.checked) {
      this.trackTLSCheck(e?.target?.checked);
    }
  };

  componentDidMount() {
    setActualPage(this.pageName);
    window.scrollTo(0, 0);
    if (this.props.onSectionLoaded) {
      this.props.onSectionLoaded();
    }
    this.trackPage();
    this.trackTLSCheck(false);
  }

  handleError(e: any) {
    if (this.props.handleError) {
      this.props.handleError(e);
    } else {
      console.error("SummaryPanel#handleError", (e && e.message) || "");
    }
  }

  getDataProtectionDocUrl() {
    return "\\assets\\docs\\leasing_dsi_data_protection.pdf";
  }

  onCloseEmailValidationModal = () => {
    this.setState({ ...this.state, showEmailValidationModal: false });
  }

  goToEmail = () => {
    this.onCloseEmailValidationModal()
    scrollToElement(Array.from(document?.querySelectorAll('.c-data-overview__title'))
      .find(el => el?.textContent === Output.resolve(
        "pages.applicationForm.panels.personalData.title"
      ))
    );
  }
  trackPageEmailValidation = () => {
    setTimeout(() => {
      const pageData = {
        [Analytics.keys.viewChange()]: 'Email check',
        [Analytics.keys.eventType(0)]: 'pageview',
        [Analytics.keys.eventAction(0)]: 'Success',
        [Analytics.keys.linkInformation(0)]: 'Email check',
      }
      Analytics.setTrackingData({ ...this.defaultTrackingData, ...pageData });
      Analytics.trackPage()
    }, 1000)
  }

  onEmailValidationSuccess = (data: any) => {
    const { onSubmit } = this.props;
    onSubmit(data?.data);
  }

  trackCreditCheckProgress = () => {
    const pageData = {
      [Analytics.keys.pageName()]: "Credit check progress",
      [Analytics.keys.viewChange()]: "Credit check progress",
      [Analytics.keys.eventType(0)]: "pageView",
      [Analytics.keys.eventAction(0)]: "Success",
      [Analytics.keys.eventType(1)]: "FinanceApproval",
      [Analytics.keys.eventAction(1)]: "Request",
    };
    Analytics.setTrackingData(pageData);
    Analytics.trackPage();
  };

  onValidation = () => {
    this.trackCreditCheckProgress();
  }

  applicationCheck = async (formValues: SummaryPageValues) => {
    const { values } = this.props;
    const checkData = {
      ...formValues,
      allowUnsafeMailCommunication:
        values?.allowUnsafeMailCommunication
          ? true
          : undefined,
    };
    setDataPrivacy(checkData);
    const sendData = {
      ...checkData,
      additionalProducts: this.props.prefill?.financialProduct?.additionalProducts?.filter((p: any) => p.isSelected)?.map((p: any) => p.code)
    };
    await finishFullAppForm(values?.storefrontTransactionId, sendData);
  };

  handleSubmit = async (values: SummaryPageValues) => {
    this.setState({ isLoading: true, formValues: values });
    try {
      await this.applicationCheck(values)
      this.setState({ isLoading: false, showEmailValidationModal: true });
      this.trackPageEmailValidation()
    } catch(error) {
      this.handleError(error)
    }
  }

  render() {
    const { values, handleEdit, tlsSupport } = this.props;

    const editButton = (section: number) => {
      return {
        buttonProps: {
          onClick: (e: any) => {
            e.preventDefault();
            this.trackEditButton(section);
            return handleEdit({ page: Route.FullApplicationForm, section });
          },
          icon: "semantic-edit",
          reversed: false,
        },
        buttonLabel: Output.resolve("pages.applicationForm.buttonEdit"),
      };
    };

    const buildBankArray = (values: any) => {
      return [
        {
          title: Output.resolve(
            "pages.applicationForm.panels.payment.fields.accountNumber.label"
          ),
          data: formatIban(values.iban),
        },
        values.bic && {
          title: Output.resolve(
            "pages.applicationForm.panels.payment.fields.accountNumberBic.label"
          ),
          data: values.bic,
        },
        values.bankName && {
          title: Output.resolve(
            "pages.applicationForm.panels.payment.fields.bankName.label"
          ),
          data: values.bankName,
        },
      ];
    };

    const renderVtiAdditionalProducts = () => {
      const { onAdditionalProductSelection, prefill } = this.props;
      const additionalProducts = prefill?.financialProduct?.additionalProducts;
      return (additionalProducts?.length && (
        <ContentSection pageWrap className="u-pb-none">
          <ContentSection.ComponentWrapper>
            <h4 className="u-text-left">
              {Output.resolve("pages.summary.additionalProducts.title")}
            </h4>
            {Output.resolve("pages.summary.additionalProducts.texts")?.map((text: string) => (
              <Paragraph className="u-text-left" key={text}>{text}</Paragraph>
            ))}
            <Layout center>
              {additionalProducts?.map((product: any) => (
                product?.isVisible ? (
                  <Layout.Item default="1/3" s="1/1" key={product?.name}>
                    <TileSelect
                      id={product?.name}
                      title={product?.name}
                      onClick={() => onAdditionalProductSelection(product)}
                      checked={product?.isSelected}
                      disabled={!product?.isSelectable}
                    >
                      <Paragraph>
                        {product?.description}
                      </Paragraph>
                      <Paragraph>
                        {product?.disclaimer}
                      </Paragraph>
                    </TileSelect>
                  </Layout.Item>
                ) : <></>
              ))}
            </Layout>
          </ContentSection.ComponentWrapper>
          <Hr className="u-mb-none" />
        </ContentSection>
      ))
    }

    return (
      <>
        <main>
          {(this.props.isSavingData || this.state?.isLoading) && <Spinner fullPage />}
          <EmailValidationModal
            transactionId={values?.transaction?.id}
            email={values?.email}
            onClose={() => this.onCloseEmailValidationModal()}
            onValidation={() => this.onValidation()}
            onSuccess={(data: any) => this.onEmailValidationSuccess(data)}
            onError={() => this.handleError(null)}
            onVerify={() => this.goToEmail()}
            visible={this.state?.showEmailValidationModal}
          />
          <ContentSection pageWrap>
            <SectionHeading level="2">
              {Output.resolve("pages.summary.title")}
            </SectionHeading>
            <Paragraph>{Output.resolve("pages.summary.subtitle")}</Paragraph>
            <ContentSection.ComponentWrapper>
              <DataOverview
                icon={"semantic-checkmark"}
                title={Output.resolve(
                  "pages.applicationForm.panels.personalData.title"
                )}
                {...editButton(0)}
              >
                <DefinitionList renderFalsy={false}>
                  {[
                    {
                      title: Output.resolve(
                        "pages.applicationForm.panels.personalData.fields.salutation.label"
                      ),
                      data: getValue(
                        Output.resolve(
                          "pages.applicationForm.panels.personalData.fields.salutation.values"
                        ),
                        values.salutation
                      ),
                    },
                    {
                      title: Output.resolve(
                        "pages.applicationForm.panels.personalData.fields.title.label"
                      ),
                      data: getValue(
                        Output.resolve(
                          "pages.applicationForm.panels.personalData.fields.title.values"
                        ),
                        values.title
                      ),
                    },
                    {
                      title: Output.resolve(
                        "pages.applicationForm.panels.personalData.fields.firstName.label"
                      ),
                      data: values.firstName,
                    },
                    {
                      title: Output.resolve(
                        "pages.applicationForm.panels.personalData.fields.surname.label"
                      ),
                      data: values.lastName,
                    },
                    {
                      title: Output.resolve(
                        "pages.applicationForm.panels.personalData.fields.maidenName.label"
                      ),
                      data: values.maidenName,
                    },
                    {
                      title: Output.resolve(
                        "pages.applicationForm.panels.personalData.fields.dateOfBirth.label"
                      ),
                      data: getGermanDate(values.birthDate),
                    },
                    {
                      title: Output.resolve(
                        "pages.applicationForm.panels.personalData.fields.placeOfBirth.label"
                      ),
                      data: values.placeOfBirth,
                    },
                    {
                      title: Output.resolve(
                        "pages.applicationForm.panels.personalData.fields.citizenship.label"
                      ),
                      data: getValue(
                        Output.resolve("generic.countrylist"),
                        values.nationality
                      ),
                    },
                  ]}
                </DefinitionList>
              </DataOverview>
            </ContentSection.ComponentWrapper>

            <ContentSection.ComponentWrapper>
              <DataOverview
                icon={"semantic-checkmark"}
                title={Output.resolve(
                  "pages.applicationForm.panels.contactData.title"
                )}
                {...editButton(1)}
              >
                <ContentSection.ComponentWrapper className="u-mb-none">
                  <DefinitionList renderFalsy={false} className="u-mb-none">
                    {[
                      {
                        title: Output.resolve(
                          "pages.applicationForm.panels.contactData.fields.street.label"
                        ),
                        data: values.street,
                      },
                      {
                        title: Output.resolve(
                          "pages.applicationForm.panels.contactData.fields.houseNumber.label"
                        ),
                        data: values.housenumber,
                      },
                      {
                        title: Output.resolve(
                          "pages.heyCar.fullAppForm.contactSection.postCode"
                        ),
                        data: values.postcode,
                      },
                      {
                        title: Output.resolve(
                          "pages.applicationForm.panels.contactData.fields.city.label"
                        ),
                        data: values.city,
                      },
                      {
                        title: Output.resolve(
                          "pages.applicationForm.panels.contactData.fields.country.label"
                        ),
                        data: getValue(
                          Output.resolve("generic.countrylist"),
                          values.country
                        ),
                      },
                      {
                        title: Output.resolve(
                          "pages.applicationForm.panels.contactData.fields.residentSince.label"
                        ),
                        data: getGermanDate(values.residentSince),
                      },
                    ]}
                  </DefinitionList>
                  {moment(values.residentSince).isAfter(
                    moment().subtract(3, "years")
                  ) && (
                    <ContentSection.ComponentWrapper className="u-mt">
                      <strong>
                        {Output.resolve(
                          "pages.summary.sections.contactData.previousAddress"
                        )}
                      </strong>
                      <br />
                      <br />
                      <DefinitionList renderFalsy={false}>
                        {[
                          {
                            title: Output.resolve(
                              "pages.applicationForm.panels.contactData.fields.street.label"
                            ),
                            data: values.prevstreet,
                          },
                          {
                            title: Output.resolve(
                              "pages.applicationForm.panels.contactData.fields.houseNumber.label"
                            ),
                            data: values.prevhousenumber,
                          },
                          {
                            title: Output.resolve(
                              "pages.applicationForm.panels.contactData.fields.postcode.label"
                            ),
                            data: values.prevpostcode,
                          },
                          {
                            title: Output.resolve(
                              "pages.applicationForm.panels.contactData.fields.city.label"
                            ),
                            data: values.prevcity,
                          },
                          {
                            title: Output.resolve(
                              "pages.applicationForm.panels.contactData.fields.country.label"
                            ),
                            data: getValue(
                              Output.resolve("generic.countrylist"),
                              values.prevcountry
                            ),
                          },
                        ]}
                      </DefinitionList>
                    </ContentSection.ComponentWrapper>
                  )}
                  {!isEuCitizen(values.nationality) && values.country === "DE" && (
                    <ContentSection.ComponentWrapper>
                      <DefinitionList renderFalsy={false}>
                        {[
                          {
                            title: Output.resolve(
                              "pages.heyCar.fullAppForm.contactSection.isLivingInDeStartDate"
                            ),
                            data: getGermanDate(values.isLivingInDeStartDate),
                          },
                          {},
                          {
                            title: Output.resolve(
                              "pages.heyCar.fullAppForm.contactSection.residencePermitStatus.label"
                            ),
                            data: getValue(
                              Output.resolve(
                                "pages.heyCar.fullAppForm.contactSection.residencePermitStatus.values"
                              ),
                              values.residencePermitStatus
                            ),
                          },
                          {
                            title: Output.resolve(
                              "pages.heyCar.fullAppForm.contactSection.workPermitStatus.label"
                            ),
                            data: getValue(
                              Output.resolve(
                                "pages.heyCar.fullAppForm.contactSection.workPermitStatus.values"
                              ),
                              values.workPermitStatus
                            ),
                          },
                          values.residencePermitEndDate && {
                            title: Output.resolve(
                              "pages.heyCar.fullAppForm.contactSection.residencePermitEndDate"
                            ),
                            data: getGermanDate(values.residencePermitEndDate),
                          },
                          values.residencePermitEndDate && {
                            title: Output.resolve(
                              "pages.heyCar.fullAppForm.contactSection.workPermitEndDate"
                            ),
                            data: getGermanDate(values.workPermitEndDate),
                          },
                        ]}
                      </DefinitionList>
                    </ContentSection.ComponentWrapper>
                  )}
                  <DefinitionList renderFalsy={false}>
                    {[
                      {
                        title: Output.resolve(
                          "pages.applicationForm.panels.contactData.fields.mail.label"
                        ),
                        data: values.email,
                      },
                      {
                        title: Output.resolve(
                          "pages.applicationForm.panels.contactData.fields.telephone.label"
                        ),
                        data: `${values.mobilePhoneCountryCode} ${values.mobilePhoneNumber}`,
                      },
                      {
                        title: Output.resolve(
                          "pages.applicationForm.panels.personalData.fields.maritalStatus.label"
                        ),
                        data: getValue(
                          Output.resolve(
                            "pages.applicationForm.panels.personalData.fields.maritalStatus.values"
                          ),
                          values.maritalStatus
                        ),
                      },
                      {
                        title: Output.resolve(
                          "pages.applicationForm.panels.personalData.fields.livingTogether.label"
                        ),
                        data: getValue(
                          Output.resolve(
                            "pages.applicationForm.panels.personalData.fields.livingTogether.values"
                          ),
                          values.livingStatus
                        ),
                      },
                    ]}
                  </DefinitionList>
                </ContentSection.ComponentWrapper>
              </DataOverview>
            </ContentSection.ComponentWrapper>

            <ContentSection.ComponentWrapper>
              <DataOverview
                icon={"semantic-checkmark"}
                title={Output.resolve(
                  "pages.applicationForm.panels.employmentData.title"
                )}
                {...editButton(2)}
              >
                <DefinitionList renderFalsy={false}>
                  {[
                    {
                      title: Output.resolve(
                        "pages.applicationForm.panels.employmentData.fields.professionGroup.label"
                      ),
                      data: getValue(
                        Output.resolve(
                          "pages.applicationForm.panels.employmentData.fields.professionGroup.values"
                        ),
                        values.professionGroup
                      ),
                    },
                    {
                      title: Output.resolve(
                        "pages.heyCar.fullAppForm.employmentSection.profession"
                      ),
                      data: values.profession,
                    },
                    {
                      title: Output.resolve(
                        "pages.applicationForm.panels.employmentData.fields.industryOfOccupation.label"
                      ),
                      data: getValue(
                        Output.resolve(
                          "pages.applicationForm.panels.employmentData.fields.industryOfOccupation.possibleValues"
                        ),
                        values.industryOfOccupation
                      ),
                    },
                    {
                      title: Output.resolve(
                        "pages.applicationForm.panels.employmentData.fields.employment.label"
                      ),
                      data: getValue(
                        Output.resolve(
                          "pages.applicationForm.panels.employmentData.fields.employment.values"
                        ),
                        values.employment
                      ),
                    },
                    {
                      title: Output.resolve(
                        "pages.applicationForm.panels.employmentData.fields.employerName.label"
                      ),
                      data: values.employerName,
                    },
                    {
                      title: Output.resolve(
                        "pages.applicationForm.panels.employmentData.fields.employerLocation.label"
                      ),
                      data: values.employerLocation,
                    },
                    {
                      title: Output.resolve(
                        "pages.applicationForm.panels.employmentData.fields.employedSince.label"
                      ),
                      data: getGermanDate(values.employedSince),
                    },
                    {
                      title: Output.resolve(
                        "pages.applicationForm.panels.employmentData.fields.employmentPeriod.label"
                      ),
                      data: getGermanDate(values.employmentLimit),
                    },
                  ]}
                </DefinitionList>
              </DataOverview>
            </ContentSection.ComponentWrapper>

            <ContentSection.ComponentWrapper>
              <DataOverview
                icon={"semantic-checkmark"}
                title={Output.resolve(
                  "pages.applicationForm.panels.expenditure.title"
                )}
                {...editButton(3)}
              >
                <ContentSection.ComponentWrapper>
                  <DefinitionList renderFalsy={false}>
                    {[
                      {
                        title: Output.resolve(
                          "pages.applicationForm.panels.expenditure.fields.netIncome.label"
                        ),
                        data: formatEuroAmount(values.netIncome),
                      },
                      {
                        title: Output.resolve(
                          "pages.applicationForm.panels.expenditure.fields.additionalNetIncome.label"
                        ),
                        data: values.additionalNetIncome
                          ? formatEuroAmount(values.additionalNetIncome)
                          : "",
                      },
                      {
                        title: Output.resolve(
                          "pages.applicationForm.panels.expenditure.fields.livingConditions.label"
                        ),
                        data: getValue(
                          Output.resolve(
                            "pages.applicationForm.panels.expenditure.fields.livingConditions.values"
                          ),
                          values.livingConditions
                        ),
                      },
                      values.livingConditions === "PROPERTY_OWNERSHIP" && {
                        title: Output.resolve(
                          "pages.applicationForm.panels.expenditure.fields.selfUsedProperty.label"
                        ),
                        data: getValue(
                          Output.resolve(
                            "pages.applicationForm.panels.expenditure.fields.selfUsedProperty.values"
                          ),
                          values.selfUsedProperty
                        ),
                      },
                      (values.livingConditions === "RENT" ||
                        (values.livingConditions === "PROPERTY_OWNERSHIP" &&
                          values.selfUsedProperty === "no")) && {
                        title: Output.resolve(
                          "pages.applicationForm.panels.expenditure.fields.monthlyRateLease.label"
                        ),
                        data: values.monthlyRateLease
                          ? formatEuroAmount(values.monthlyRateLease)
                          : "",
                      },
                      values.livingConditions === "PROPERTY_OWNERSHIP" && {
                        title: Output.resolve(
                          "pages.applicationForm.panels.expenditure.fields.monthlyRateProperty.label"
                        ),
                        data: values.monthlyRateProperty
                          ? formatEuroAmount(values.monthlyRateProperty)
                          : "",
                      },
                      {
                        title: Output.resolve(
                          "pages.applicationForm.panels.expenditure.fields.extraCost.label"
                        ),
                        data: formatEuroAmount(values.extraCost),
                      },
                      {
                        title: Output.resolve(
                          "pages.applicationForm.panels.expenditure.fields.alimentChildrenCount.label"
                        ),
                        data: values.alimentChildrenCount,
                      },
                      {
                        title: Output.resolve(
                          "pages.applicationForm.panels.expenditure.fields.activeCreditContracts.label"
                        ),
                        data: getValue(
                          Output.resolve(
                            "pages.applicationForm.panels.expenditure.fields.activeCreditContracts.values"
                          ),
                          values.creditsAndLoans
                        ),
                      },
                      values.creditsAndLoans === "yes" && {
                        title: Output.resolve(
                          "pages.heyCar.fullAppForm.expenditureSection.monthlyCreditRate.label"
                        ),
                        data: values.monthlyCreditRate,
                      },
                      {
                        title: Output.resolve(
                          "pages.heyCar.fullAppForm.expenditureSection.isMajorityShareholder.label"
                        ),
                        data: getValue(
                          Output.resolve(
                            "pages.heyCar.fullAppForm.expenditureSection.isMajorityShareholder.values"
                          ),
                          values.isMajorityShareholder
                        ),
                      },
                      values.isMajorityShareholder === "yes" && {
                        title: Output.resolve(
                          "pages.heyCar.fullAppForm.expenditureSection.companiesName"
                        ),
                        data: values.majorityShareholderCompanyName,
                      },
                      values.isMajorityShareholder === "yes" && {
                        title: Output.resolve(
                          "pages.heyCar.fullAppForm.expenditureSection.companiesCity"
                        ),
                        data: values.majorityShareholderCompanyCity,
                      },
                      {
                        title: Output.resolve(
                          "pages.heyCar.fullAppForm.expenditureSection.isLiablePartner.label"
                        ),
                        data: getValue(
                          Output.resolve(
                            "pages.heyCar.fullAppForm.expenditureSection.isLiablePartner.values"
                          ),
                          values.isLiablePartner
                        ),
                      },
                      values.isLiablePartner === "yes" && {
                        title: Output.resolve(
                          "pages.heyCar.fullAppForm.expenditureSection.companiesName"
                        ),
                        data: values.liablePartnerCompanyName,
                      },
                      values.isLiablePartner === "yes" && {
                        title: Output.resolve(
                          "pages.heyCar.fullAppForm.expenditureSection.companiesCity"
                        ),
                        data: values.liablePartnerCompanyCity,
                      },
                    ]}
                  </DefinitionList>
                </ContentSection.ComponentWrapper>
              </DataOverview>
            </ContentSection.ComponentWrapper>

            <ContentSection.ComponentWrapper>
              <DataOverview
                icon={"semantic-checkmark"}
                title={Output.resolve(
                  "pages.applicationForm.panels.payment.title"
                )}
                {...editButton(4)}
              >
                <DefinitionList renderFalsy={false}>
                  {buildBankArray(values)}
                </DefinitionList>
              </DataOverview>
            </ContentSection.ComponentWrapper>
          </ContentSection>

          {isVtiBOP(this.props.originSystem) && renderVtiAdditionalProducts()}

          <ContentSection pageWrap>
            <Formik
              initialValues={this.state.values}
              isInitialValid={false}
              onSubmit={this.handleSubmit}
              validate={(values) => {
                if (tlsSupport) {
                  return;
                }
              }}
              render={({ values, setValues, handleSubmit }) => (
                <Form onSubmit={handleSubmit} floatingLabel>
                  <Fieldset.Row>
                    <h4 className="u-h4">
                      {Output.resolve("pages.summary.dse.title")}
                    </h4>
                    <>
                      {Output.resolve("pages.summary.dse.text1")}
                      <a
                        href={this.getDataProtectionDocUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {Output.resolve("pages.summary.dse.linkText")}
                      </a>
                      {Output.resolve("pages.summary.dse.text2")}
                    </>
                  </Fieldset.Row>
                  <Fieldset.Row>
                    <FastField
                      name="allowAllAds"
                      label={Output.resolve(
                        "pages.summary.dse.allowAllAdsLabel"
                      )}
                      component={CheckboxField}
                      beforeChange={(e: any) => {
                        setValues({
                          ...values,
                          allowPhoneAds: e?.target?.checked,
                          allowMailAds: e?.target?.checked,
                          allowPersonalizedMarketing: e?.target?.checked,
                        });
                      }}
                    />
                  </Fieldset.Row>
                  <Fieldset.Row>
                    <b>
                      {Output.resolve("pages.summary.dse.electronicAds.title")}
                    </b>
                  </Fieldset.Row>
                  <Fieldset.Row>
                    {Output.resolve("pages.summary.dse.electronicAds.text1")}
                  </Fieldset.Row>
                  <Fieldset.Row>
                    <FastField
                      name="allowPhoneAds"
                      label={Output.resolve(
                        "pages.summary.dse.electronicAds.allowPhoneAdsLabel"
                      )}
                      component={CheckboxField}
                      beforeChange={(e: any) => {
                        if (!e?.target?.checked) {
                          setValues({
                            ...values,
                            allowAllAds: false,
                          });
                        }
                      }}
                    />
                  </Fieldset.Row>
                  <Fieldset.Row>
                    <FastField
                      name="allowMailAds"
                      label={Output.resolve(
                        "pages.summary.dse.electronicAds.allowMailAdsLabel"
                      )}
                      component={CheckboxField}
                      beforeChange={(e: any) => {
                        if (!e?.target?.checked) {
                          setValues({
                            ...values,
                            allowAllAds: false,
                          });
                        }
                      }}
                    />
                  </Fieldset.Row>
                  <Fieldset.Row>
                    <ShowMore
                      labelCollapse={Output.resolve(
                        "pages.summary.dse.showMoreCollapse"
                      )}
                      labelExpand={Output.resolve(
                        "pages.summary.dse.showMoreExpand"
                      )}
                    >
                      <Paragraph>
                        {Output.resolve(
                          "pages.summary.dse.electronicAds.showMoreContent1"
                        )}
                      </Paragraph>
                      <Paragraph className="u-font-size-fs-2">
                        {Output.resolve(
                          "pages.summary.dse.electronicAds.showMoreContent2"
                        )}
                      </Paragraph>
                      <Paragraph className="u-font-size-fs-2">
                        {Output.resolve(
                          "pages.summary.dse.electronicAds.showMoreContent3"
                        )}
                      </Paragraph>
                    </ShowMore>
                  </Fieldset.Row>
                  <Fieldset.Row>
                    <b>
                      {Output.resolve(
                        "pages.summary.dse.personalizedMarketing.title"
                      )}
                    </b>
                  </Fieldset.Row>
                  <Fieldset.Row>
                    <FastField
                      name="allowPersonalizedMarketing"
                      label={Output.resolve(
                        "pages.summary.dse.personalizedMarketing.allowPersonalizedMarketingLabel"
                      )}
                      component={CheckboxField}
                      beforeChange={(e: any) => {
                        if (!e?.target?.checked) {
                          setValues({
                            ...values,
                            allowAllAds: false,
                          });
                        }
                      }}
                    />
                  </Fieldset.Row>
                  <Fieldset.Row>
                    <ShowMore
                      labelCollapse={Output.resolve(
                        "pages.summary.dse.showMoreCollapse"
                      )}
                      labelExpand={Output.resolve(
                        "pages.summary.dse.showMoreExpand"
                      )}
                    >
                      <Paragraph>
                        {Output.resolve(
                          "pages.summary.dse.personalizedMarketing.showMoreContent1"
                        )}
                      </Paragraph>
                      <Paragraph className="u-font-size-fs-2">
                        {Output.resolve(
                          "pages.summary.dse.personalizedMarketing.showMoreContent2"
                        )}
                      </Paragraph>
                    </ShowMore>
                  </Fieldset.Row>

                  {tlsSupport === false && (
                    <>
                      <Fieldset>
                        <Fieldset.Row>
                          {Output.resolve(
                            "pages.summary.dse.emailComunication.text1"
                          )}
                        </Fieldset.Row>
                        <Fieldset.Row>
                          <b>
                            {Output.resolve(
                              "pages.summary.dse.emailComunication.title"
                            )}
                          </b>
                        </Fieldset.Row>
                        <Fieldset.Row>
                          {Output.resolve(
                            "pages.summary.dse.emailComunication.text2"
                          )}
                        </Fieldset.Row>
                        <Fieldset.Row>
                          <FastField
                            name="allowUnsafeMailCommunication"
                            label={Output.resolve(
                              "pages.summary.dse.emailComunication.allowMailCommunicationLabel"
                            )}
                            component={CheckboxField}
                          />
                        </Fieldset.Row>
                      </Fieldset>
                    </>
                  )}
                  <hr />
                  <Paragraph>{Output.resolve("pages.summary.confirmationText")}</Paragraph>
                  <div className="o-button-container o-button-container--fixed o-button-container--right">
                    <Button
                      disabled={
                        tlsSupport === false &&
                        !values.allowUnsafeMailCommunication
                      }
                      type="submit"
                      small
                      testId="summary-global-CTA"
                    >
                      {Output.resolve("pages.summary.buttonSubmit")}
                    </Button>
                  </div>
                </Form>
              )}
            />
          </ContentSection>
        </main>
      </>
    );
  }
}

type SummaryPageProps = {
  tlsSupport: boolean;
  values: any;
  handleEdit: (params: { page: string; section: number }) => void;
  onSectionLoaded: () => void;
  onSubmit: (data: any) => void;
  handleError: (error: any) => void;
  isSavingData: boolean;
  companyId: string;
  originSystem: string;
  vehicleData: any;
  dealerData: any;
  prefill: any;
  onAdditionalProductSelection: Function;
};

export interface SummaryPageValues {
  allowAllAds: boolean | undefined;
  allowPhoneAds: boolean | undefined;
  allowMailAds: boolean | undefined;
  allowPersonalizedMarketing: boolean | undefined;
  allowUnsafeMailCommunication: boolean | undefined;
}

type SummaryPageState = {
  values: SummaryPageValues;
  showEmailValidationModal: boolean;
  isLoading: boolean;
  formValues: any;
};

export { SummaryPage };
